import React, { useState } from 'react';
import {
  AiChat02Icon,
  BookOpen01Icon,
  Compass01Icon,
  CustomerSupportIcon,
  HelpCircleIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { openHelpChat } from '../controllers/docshoundChat';
import AIBorder from './AIBorder';
import { openSupportDrawer, SUPPORT_TYPES } from '../../redux/slices/supportDrawerSlice';
import { startTour } from '../../redux/slices/tourSlice';
import TourMenu from '../../components/tour/TourMenu';

const DOCS_URL = 'https://docs.bridged.media/introduction/getting-started-with-bridged';

/** One Help option: tinted icon tile, label and a short line on what it opens. */
function HelpOption({ icon, label, hint, onSelect, autoFocus }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      autoFocus={autoFocus}
      className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-start outline-none transition-colors duration-150 ease-out-quad hover:bg-foreground/[0.06] focus-visible:bg-foreground/[0.06] focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <HugeiconsIcon icon={icon} strokeWidth={2} className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-[13px] text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}

function ChatbotTriggerButton() {
  const dispatch = useDispatch();
  const [helpOpen, setHelpOpen] = useState(false);
  const [view, setView] = useState('help');
  const [backFromTours, setBackFromTours] = useState(false);

  // Close the menu first so focus returns to Help before the chosen surface opens.
  const pick = action => () => {
    setHelpOpen(false);
    action();
  };

  return (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => dispatch(openSupportDrawer(SUPPORT_TYPES.SUPPORT))}
            aria-label="Contact support"
          >
            <HugeiconsIcon icon={CustomerSupportIcon} strokeWidth={2} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Support</TooltipContent>
      </Tooltip>

      {/* Highlighted: primary fill with the shared AI shine ring drawn just outside it
          (1px gap), so the ring reads against the header with any brand colour. */}
      <AIBorder
        borderRadius={9999}
        duration={6}
        color={['var(--shine-1)', 'var(--shine-2)', 'var(--shine-3)']}
        className="ms-1 p-[3px]"
      >
        <Popover
          open={helpOpen}
          onOpenChange={next => {
            setHelpOpen(next);
            if (next) {
              setView('help');
              setBackFromTours(false);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="sm"
              // Icon-only on phones so the breadcrumb keeps its room.
              className="max-sm:size-8 max-sm:px-0!"
              aria-label="Help"
              data-tour="help"
            >
              <HugeiconsIcon icon={HelpCircleIcon} strokeWidth={2} data-icon="inline-start" />
              <span className="max-sm:sr-only">Help</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className={view === 'tours' ? 'w-80 gap-0 p-1.5' : 'w-72 gap-0.5 p-1.5'}
          >
            {view === 'tours' ? (
              <TourMenu
                onBack={() => {
                  setBackFromTours(true);
                  setView('help');
                }}
                onStart={tourId => pick(() => dispatch(startTour(tourId)))()}
              />
            ) : (
              <>
                <HelpOption
                  icon={AiChat02Icon}
                  label="Ask AI"
                  hint="Answers from the docs"
                  onSelect={pick(openHelpChat)}
                />
                <HelpOption
                  icon={Compass01Icon}
                  label="Guided tours"
                  hint="Learn your way around"
                  onSelect={() => setView('tours')}
                  autoFocus={backFromTours}
                />
                <HelpOption
                  icon={BookOpen01Icon}
                  label="Documentation"
                  hint="Guides for every agent"
                  onSelect={pick(() => window.open(DOCS_URL, '_blank', 'noopener,noreferrer'))}
                />
                <HelpOption
                  icon={CustomerSupportIcon}
                  label="Contact support"
                  hint="Message our team"
                  onSelect={pick(() => dispatch(openSupportDrawer(SUPPORT_TYPES.SUPPORT)))}
                />
              </>
            )}
          </PopoverContent>
        </Popover>
      </AIBorder>
    </div>
  );
}

export default ChatbotTriggerButton;
